/**
 * pago-list.ts — APR-8 (openspec/changes/cuentas-pagar-historial-y-gestion-pagos).
 * Request/response contract for `GET /sumi/cuentas-pagar/pagos` ("Historial de
 * pagos"). `medioPago` stays a plain string on READ (historical rows hold
 * free text, so the column is still `text`); only the write path
 * (`dto.ts` `pagoNuevo.medioPago`) is the closed `MEDIOS_PAGO` vocabulary. The
 * query filter is therefore an exact string match, not an enum.
 *
 * Spec: openspec/changes/cuentas-pagar-historial-y-gestion-pagos/specs/cuentas-pagar-http-routes/spec.md APR-8
 * Design: openspec/changes/cuentas-pagar-historial-y-gestion-pagos/design.md "Interfaces / Contracts"
 */
import { z } from 'zod';

export const PagoListImputacionSchema = z.object({
  comprobanteId: z.string().uuid(),
  montoAplicado: z.string(),
});
export type PagoListImputacion = z.infer<typeof PagoListImputacionSchema>;

export const PagoListRowSchema = z.object({
  movimientoId: z.string().uuid(),
  proveedorId: z.string().uuid(),
  proveedorNombre: z.string(),
  proveedorCuit: z.string().nullable(),
  monto: z.string(),
  medioPago: z.string().nullable(),
  createdAt: z.string(),
  comprobantesImputados: z.array(PagoListImputacionSchema),
  /** retenciones-en-el-pago APR-8 — the Orden de Pago the pago belongs to; null for a historic pago. */
  ordenPago: z.object({ id: z.string().uuid(), numero: z.number().int().positive() }).nullable(),
  /** ARS totals of the whole order: what it cancelled, what was withheld, what was actually paid. */
  totalCancelado: z.string(),
  totalRetenido: z.string(),
  totalPagado: z.string(),
  /**
   * True once the pago was annulled (by "Anular" or replaced by "Editar pago"). Default false so a SPA deployed before
   * the backend still parses the older response.
   */
  anulado: z.boolean().default(false),
});
export type PagoListRow = z.infer<typeof PagoListRowSchema>;

export const PagoListResponseSchema = z.object({
  items: z.array(PagoListRowSchema),
});
export type PagoListResponse = z.infer<typeof PagoListResponseSchema>;

/** Query param validation for `GET /sumi/cuentas-pagar/pagos?proveedorId&fechaDesde&fechaHasta&medioPago`. */
export const PagoListQuerySchema = z.object({
  proveedorId: z.string().uuid().optional(),
  fechaDesde: z
    .string()
    .refine((s) => !Number.isNaN(new Date(s).getTime()), 'fechaDesde inválida')
    .optional(),
  fechaHasta: z
    .string()
    .refine((s) => !Number.isNaN(new Date(s).getTime()), 'fechaHasta inválida')
    .optional(),
  medioPago: z.string().max(200).optional(),
});
export type PagoListQuery = z.infer<typeof PagoListQuerySchema>;
